import {
  ContentItem,
  ContentItems,
  ContentWrapper,
  Text,
  TitledText,
} from '@/components';
import {
  formatOwnershipRatioFraction,
  formatOwnershipRatioPartsOfFraction,
  formatOwnershipRatioPercentage,
} from '@/features/mold/utils';
import {
  DiscriminatedInspectionEvaluationModel,
  ExistenceOrAbsenceLabelModifier,
  ProjectModel,
} from '@/features/project/projects/_models/project';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

const PropsModel = ProjectModel.pick({
  inspectionEvaluation: true,
});

type Props = z.infer<typeof PropsModel>;

export const KsStandardInspectionEvaluationBefore2024: FC<Props> = ({
  inspectionEvaluation,
}) => {
  const { t } = useTranslation();
  const parsed = DiscriminatedInspectionEvaluationModel.safeParse({
    sheetType: 'KS',
    inspectionEvaluation,
  });

  if (!parsed.success || parsed.data.sheetType !== 'KS') {
    return null;
  }

  const {
    inspectionAndImprovementByAdministrativeJurisdiction,
    ownershipRatio,
    lowUsedFundInvestigation,
    ...rest
  } = parsed.data.inspectionEvaluation;

  return (
    <>
      <ContentWrapper title={t('self-inspection-by-fund-jurisdiction')}>
        <ContentItem borderBottom={0} paddingBottom={0}>
          <ContentItems
            mode="fillLeft"
            items={[
              <TitledText
                key="ownership-ratio"
                title={t('ownership-ratio')}
                text={
                  ownershipRatio?.operationType === 'auto'
                    ? formatOwnershipRatioFraction(
                        ownershipRatio.ratioNumerator,
                        ownershipRatio.ratioDenominator,
                      )
                    : formatOwnershipRatioPercentage(
                        ownershipRatio?.manualRatio,
                      )
                }
              />,
              ...(ownershipRatio?.operationType === 'auto'
                ? [
                    <TitledText
                      key="ownership-ratio-numerator"
                      title={t('ownership-ratio-numerator-before-2025')}
                      text={formatOwnershipRatioPartsOfFraction(
                        ownershipRatio.ratioNumerator,
                      )}
                    />,
                    <TitledText
                      key="ownership-ratio-denominator"
                      title={t('ownership-ratio-denominator-before-2025')}
                      text={formatOwnershipRatioPartsOfFraction(
                        ownershipRatio.ratioDenominator,
                      )}
                    />,
                  ]
                : []),
            ]}
          />
        </ContentItem>
      </ContentWrapper>
      <ContentWrapper title={t('ownership-ratio')}>
        <ContentItem borderBottom={0} paddingBottom={0}>
          <ContentItems
            style={{ marginBottom: 24 }}
            items={[
              <TitledText
                key="basis-calculation-formula"
                title={t('basis-calculation-formula')}
                text={ownershipRatio?.basis?.calculationFormula}
              />,
              <TitledText
                key="contents-of-each-term"
                title={t('contents-of-each-term')}
                text={ownershipRatio?.basis?.contents}
              />,
            ]}
          />

          <ContentItems
            style={{ marginBottom: 24 }}
            items={[
              <TitledText
                key="concept-of-basis-calculation-formula"
                title={t('concept-of-basis-calculation-formula')}
                text={
                  ownershipRatio?.conceptOfProjectProspect?.calculationFormula
                }
              />,
              <TitledText
                key="concept-of-contents-of-each-term"
                title={t('concept-of-contents-of-each-term-before-2025')}
                text={ownershipRatio?.conceptOfProjectProspect?.contents}
              />,
            ]}
          />

          <ContentItems
            items={[
              <TitledText
                key="basis-for-estimating-indicators-for-project-prospect"
                title={t(
                  'basis-for-estimating-indicators-for-project-prospect',
                )}
                text={
                  ownershipRatio?.conceptOfProjectProspect
                    ?.basisForCalculationOfIndicators
                }
              />,
              <TitledText
                key="indicators-for-project-prospect-and-recent-performance"
                title={t(
                  'indicators-for-project-prospect-and-recent-performance',
                )}
                text={
                  ownershipRatio?.conceptOfProjectProspect?.moreRecentResults
                }
              />,
            ]}
          />
        </ContentItem>
      </ContentWrapper>

      <ContentWrapper title={t('low-used-fund-investigation')}>
        <ContentItem borderBottom={0} paddingBottom={0}>
          <ContentItems
            style={{ marginBottom: 24 }}
            items={[
              <TitledText
                key="has-ceased-operations"
                title={t('has-ceased-operations')}
                text={
                  typeof lowUsedFundInvestigation?.hasCeasedOperations ===
                  'boolean'
                    ? lowUsedFundInvestigation.hasCeasedOperations
                      ? ExistenceOrAbsenceLabelModifier.true
                      : ExistenceOrAbsenceLabelModifier.false
                    : null
                }
              />,
              <TitledText
                key="has-no-operation-fund-detected"
                title={t('has-no-operation-fund-detected')}
                text={
                  typeof lowUsedFundInvestigation?.hasNoResultRecent ===
                  'boolean'
                    ? lowUsedFundInvestigation.hasNoResultRecent
                      ? ExistenceOrAbsenceLabelModifier.true
                      : ExistenceOrAbsenceLabelModifier.false
                    : null
                }
              />,
            ]}
          />

          <ContentItems
            style={{ marginBottom: 24 }}
            items={[
              <TitledText
                key="has-fund-that-lost-purpose"
                title={t('has-fund-that-lost-purpose')}
                text={
                  typeof lowUsedFundInvestigation?.hasFundThatLostPurpose ===
                  'boolean'
                    ? lowUsedFundInvestigation.hasFundThatLostPurpose
                      ? ExistenceOrAbsenceLabelModifier.true
                      : ExistenceOrAbsenceLabelModifier.false
                    : null
                }
              />,
              <TitledText
                key="has-exceed-1-significantly"
                title={t('has-exceed-1-significantly')}
                text={
                  typeof lowUsedFundInvestigation?.hasExceedOneSignificantly ===
                  'boolean'
                    ? lowUsedFundInvestigation.hasExceedOneSignificantly
                      ? ExistenceOrAbsenceLabelModifier.true
                      : ExistenceOrAbsenceLabelModifier.false
                    : null
                }
              />,
            ]}
          />

          <ContentItems
            style={{ marginBottom: 24 }}
            items={[
              <TitledText
                key="has-deemed-unlikely-to-be-used"
                title={t('has-deemed-unlikely-to-be-used')}
                text={
                  typeof lowUsedFundInvestigation?.hasDeemedUnlikelyToBeUsed ===
                  'boolean'
                    ? lowUsedFundInvestigation.hasDeemedUnlikelyToBeUsed
                      ? ExistenceOrAbsenceLabelModifier.true
                      : ExistenceOrAbsenceLabelModifier.false
                    : null
                }
              />,
            ]}
          />

          <ContentItems
            style={{ marginBottom: 24 }}
            items={[
              <TitledText
                key="reason-for-selecting-absense-when-ownership-ratio-exceeds-1"
                title={t(
                  'reason-for-selecting-absense-when-ownership-ratio-exceeds-1',
                )}
                text={
                  lowUsedFundInvestigation?.reasonsForBeingFalseWhileExceedingOne
                }
              />,
              <TitledText
                key="consideration-where-the-fund-are-unlikely-to-be-used"
                title={t(
                  'consideration-where-the-fund-are-unlikely-to-be-used',
                )}
                text={lowUsedFundInvestigation?.resultsOfInvestigations}
              />,
            ]}
          />

          <ContentItems
            items={[
              <TitledText
                key="reason-for-leaving-the-fund-that-are-unlikely-to-be-used"
                title={t(
                  'reason-for-leaving-the-fund-that-are-unlikely-to-be-used',
                )}
                text={lowUsedFundInvestigation?.reasonsForLeaving}
              />,
            ]}
          />
        </ContentItem>
      </ContentWrapper>

      <ContentWrapper title={t('appropriateness-inspection')}>
        <ContentItem borderBottom={0} paddingBottom={0}>
          <ContentItems
            items={[
              <TitledText
                key="reason-why-bulk-delivering-was-necessary"
                title={t('reason-why-bulk-delivering-was-necessary')}
                text={
                  parsed.data.inspectionEvaluation.appropriatenessInspection
                    ?.reasonsWhyBulkDeliveringWasNecessary
                }
              />,
              <TitledText
                key="approach-in-determining-the-timing-and-amount-of-additions"
                title={t(
                  'approach-in-determining-the-timing-and-amount-of-additions',
                )}
                text={
                  parsed.data.inspectionEvaluation.appropriatenessInspection
                    ?.approachInDeterminingTheTimingAndAmountOfAdditions
                }
              />,
            ]}
          />
        </ContentItem>
      </ContentWrapper>

      <ContentWrapper title={t('implementation-status-of-inspection')}>
        <ContentItem borderBottom={0} paddingBottom={0}>
          <ContentItems
            items={[
              <Text
                key="inspectionEvaluation.implementationStatusOfInspection"
                variant="body"
                text={
                  parsed.data.inspectionEvaluation
                    .implementationStatusOfInspection
                }
                weight="regular"
              />,
            ]}
          />
        </ContentItem>
      </ContentWrapper>

      <ContentWrapper title={t('eligibility-inspection')}>
        <ContentItem borderBottom={0} paddingBottom={0}>
          <ContentItems
            items={[
              <TitledText
                key="eligibility-inspection-method-and-reasons-for-selection"
                title={t(
                  'eligibility-inspection-method-and-reasons-for-selection-before-2025',
                )}
                text={
                  parsed.data.inspectionEvaluation.eligibilityInspection
                    ?.methodAndReasonsForSelection
                }
              />,
              <TitledText
                key="eligibility-inspection-results"
                title={t('eligibility-inspection-results-before-2025')}
                text={
                  parsed.data.inspectionEvaluation.eligibilityInspection
                    ?.results
                }
              />,
            ]}
          />
        </ContentItem>
      </ContentWrapper>

      <ContentWrapper
        title={t('inspection-and-improvement-by-fund-jurisdiction')}
      >
        <ContentItem borderBottom={0} paddingBottom={0}>
          <ContentItems
            style={{ marginBottom: 24 }}
            items={[
              <TitledText
                key="inspection-result"
                title={t('inspection-result')}
                text={
                  inspectionAndImprovementByAdministrativeJurisdiction?.inspectionResult
                }
              />,
              <TitledText
                key="evaluation"
                title={t('evaluation')}
                text={
                  inspectionAndImprovementByAdministrativeJurisdiction?.evaluation
                }
              />,
            ]}
          />

          <ContentItems
            items={[
              <TitledText
                key="direction-of-improvement"
                title={t('direction-of-improvement')}
                text={
                  inspectionAndImprovementByAdministrativeJurisdiction?.directionOfImprovement
                }
              />,
            ]}
          />
        </ContentItem>
      </ContentWrapper>

      <ContentWrapper
        title={t('inspection-by-other-than-fund-jurisdiction')}
        borderBottom={0}
      >
        <ContentItem borderBottom={0} paddingBottom={0}>
          <ContentItems
            style={{ marginBottom: 24 }}
            items={[
              <TitledText
                key="findings-of-external-experts"
                title={t('findings-of-external-experts')}
                text={rest.findingsOfExternalExperts}
              />,
              <TitledText
                key="process-leading-to-the-findings-of-review-promotion-team"
                title={t(
                  'process-leading-to-the-findings-of-review-promotion-team',
                )}
                text={rest.processLeadingToTheFindingsOfReviewPromotionTeam}
              />,
            ]}
          />

          <ContentItems
            items={[
              <TitledText
                key="improvement-point-based-on-the-findings"
                title={t('improvement-point-based-on-the-findings')}
                text={rest.improvementPointBasedOnTheFindings}
              />,
              <TitledText
                key="overview-of-reviews-carried-out-in-the-past"
                title={t('overview-of-reviews-carried-out-in-the-past')}
                text={rest.overviewOfReviewsCarriedOutInThePast}
              />,
            ]}
          />
        </ContentItem>
      </ContentWrapper>
    </>
  );
};
